import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Minus, Plus } from 'lucide-react';

/**
 * V3 — Modal for donating slices to a work.
 *
 * Props:
 *   isOpen       — boolean
 *   onClose      — () => void
 *   work         — { id, title } — the work being donated to
 *   balance      — current slice balance (integer)
 *   isActiveSubscriber — whether the user has an active subscription
 *   onSuccess    — (newBalance: number) => void — called after successful donation
 */
const DonateSlicesModal = ({ isOpen, onClose, work, balance, isActiveSubscriber, onSuccess }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const safeBalance = balance ?? 0;

  // Reset amount when modal opens or balance changes
  useEffect(() => {
    if (isOpen) setAmount(1);
  }, [isOpen]);

  const clamped = Math.max(1, Math.min(amount, safeBalance));

  const setAmountSafe = (val) => {
    const n = parseInt(val, 10);
    if (!isNaN(n)) setAmount(Math.max(1, Math.min(n, safeBalance)));
  };

  const handleDonate = async () => {
    if (clamped < 1 || clamped > safeBalance) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('donate_slices_to_work', {
        p_work_id: work.id,
        p_amount: clamped,
      });
      if (error) throw error;

      if (data?.success) {
        // Notify all useSliceBalance instances to refetch
        window.dispatchEvent(new Event('sliceBalanceChanged'));
        toast({
          title: `${clamped} slice${clamped !== 1 ? 's' : ''} donated!`,
          description: `Thank you for supporting ${work.title}.`,
        });
        onSuccess?.(data.new_balance);
        onClose();
      } else {
        toast({
          title: 'Donation failed',
          description: data?.error || 'Something went wrong.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to donate slices.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // No subscription — show upgrade prompt
  const renderNoSubscription = () => (
    <>
      <DialogHeader>
        <DialogTitle>Subscribe to Donate Slices</DialogTitle>
        <DialogDescription>
          Slice donations are included with all subscription plans. Subscribe to support creators directly.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => { onClose(); navigate('/pricing'); }}>
          View Plans
        </Button>
      </DialogFooter>
    </>
  );

  // Subscribed but balance is 0
  const renderNoBalance = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span>🍕</span> No Slices Available
        </DialogTitle>
        <DialogDescription>
          You've used all your slices for this month. Your balance resets on your next billing date.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4">
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button variant="outline" onClick={() => { onClose(); navigate('/slices'); }}>
          View History
        </Button>
      </DialogFooter>
    </>
  );

  const QUICK_AMOUNTS = [1, 3, 5];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        {!isActiveSubscriber ? (
          renderNoSubscription()
        ) : safeBalance === 0 ? (
          renderNoBalance()
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>🍕</span> Donate Slices
              </DialogTitle>
              <DialogDescription>
                Supporting{' '}
                <span className="font-medium text-foreground">{work?.title}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Balance */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Your balance</span>
                <span className="font-semibold">🍕 {safeBalance} slices</span>
              </div>

              {/* Amount selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Amount to donate</label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setAmount(a => Math.max(1, a - 1))}
                    disabled={clamped <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <input
                    type="number"
                    min={1}
                    max={safeBalance}
                    value={clamped}
                    onChange={e => setAmountSafe(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-center text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setAmount(a => Math.min(a + 1, safeBalance))}
                    disabled={clamped >= safeBalance}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Quick select */}
                <div className="flex gap-2 flex-wrap">
                  {QUICK_AMOUNTS.map(q => (
                    <Button
                      key={q}
                      type="button"
                      variant={clamped === q ? 'default' : 'outline'}
                      size="sm"
                      className="text-xs h-7 px-3"
                      onClick={() => setAmountSafe(q)}
                      disabled={q > safeBalance}
                    >
                      {q}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant={clamped === safeBalance ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7 px-3"
                    onClick={() => setAmountSafe(safeBalance)}
                  >
                    All {safeBalance}
                  </Button>
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-lg bg-muted/50 border border-border/50 p-3 text-sm space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Donating</span>
                  <span className="font-semibold text-primary">🍕 {clamped} slices</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Balance after</span>
                  <span className="font-medium">🍕 {safeBalance - clamped} slices</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleDonate}
                disabled={isLoading}
              >
                {isLoading
                  ? 'Donating…'
                  : `Donate ${clamped} Slice${clamped !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DonateSlicesModal;
